import React, { useState, useEffect, useCallback, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  FileCheck,
  Upload,
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle,
  Loader2,
  RefreshCw,
  Trash2,
  Shield
} from "lucide-react";

const REQUIRED_DOCUMENTS = [
  { type: "id_proof", name: "ID Proof (Aadhaar/PAN)", required: true },
  { type: "driving_license", name: "Driving License", required: true },
  { type: "vehicle_rc", name: "Vehicle Registration (RC)", required: true },
  { type: "police_clearance", name: "Police Clearance Certificate", required: true },
  { type: "profile_photo", name: "Profile Photo", required: true },
  { type: "bank_details", name: "Bank Account Details", required: true },
  { type: "business_registration", name: "Business Registration/GST", required: false },
];

export default function PartnerVerification() {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [uploading, setUploading] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const fileInputRefs = useRef({});

  const fetchVerificationStatus = useCallback(async () => {
    try {
      const response = await api.get("/partner-verification/status");
      setVerificationStatus(response.data);
      setDocuments(response.data.documents || []);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Failed to fetch verification status:', error.message);
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchVerificationStatus();
  }, [fetchVerificationStatus]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchVerificationStatus();
    toast.success("Status refreshed");
  };

  const handleFileSelect = async (docType, file) => {
    if (!file) return;

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File size must be less than 5MB");
      return;
    }

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Only JPEG, PNG, WebP, and PDF files are allowed");
      return;
    }

    setUploading(docType);

    try {
      // Convert file to base64
      const reader = new FileReader();
      reader.onload = async () => {
        const base64 = reader.result.split(',')[1];
        
        await api.post("/partner-verification/upload", {
          document_type: docType,
          file_data: base64,
          file_name: file.name
        });

        toast.success("Document uploaded successfully");
        fetchVerificationStatus();
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload document");
    } finally {
      setUploading(null);
    }
  };

  const handleDelete = async (docType) => {
    if (!window.confirm("Are you sure you want to delete this document?")) {
      return;
    }

    setDeleting(docType);
    try {
      await api.delete(`/partner-verification/documents/${docType}`);
      toast.success("Document deleted");
      fetchVerificationStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete document");
    } finally {
      setDeleting(null);
    }
  };

  const getDocumentStatus = (docType) => {
    const doc = documents.find(d => d.document_type === docType);
    return doc?.status || 'not_uploaded';
  };

  const getDocumentInfo = (docType) => {
    return documents.find(d => d.document_type === docType);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'pending':
        return <Clock className="w-5 h-5 text-yellow-500" />;
      case 'rejected':
        return <XCircle className="w-5 h-5 text-red-500" />;
      default:
        return <AlertCircle className="w-5 h-5 text-gray-400" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'bg-green-100 text-green-800 border-green-200';
      case 'pending': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'rejected': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-10">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-black tracking-tight">Account Verification</h1>
        <Button 
          variant="outline" 
          onClick={handleRefresh}
          disabled={refreshing}
          className="rounded-none gap-2"
          data-testid="refresh-verification-btn"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Verification Status Banner */}
      {verificationStatus && (
        <div className={`border p-6 ${
          verificationStatus.can_operate 
            ? 'bg-green-50 border-green-200' 
            : 'bg-amber-50 border-amber-200'
        }`}>
          <div className="flex items-start gap-4">
            <div className={`w-12 h-12 flex items-center justify-center ${
              verificationStatus.can_operate ? 'bg-green-100' : 'bg-amber-100'
            }`}>
              <Shield className={`w-6 h-6 ${
                verificationStatus.can_operate ? 'text-green-600' : 'text-amber-600'
              }`} />
            </div>
            <div>
              <p className="font-bold text-lg">
                {verificationStatus.can_operate ? 'Account Verified' : 'Verification Required'}
              </p>
              <p className={`text-sm ${
                verificationStatus.can_operate ? 'text-green-700' : 'text-amber-700'
              }`}>
                {verificationStatus.message}
              </p>
              <span className={`inline-block mt-2 px-3 py-1 text-xs font-bold uppercase ${getStatusColor(verificationStatus.verification_status)}`}>
                {verificationStatus.verification_status}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Document Upload Cards */}
      <div className="space-y-4">
        <h2 className="text-xl font-bold">Required Documents</h2>
        
        <div className="grid gap-4">
          {REQUIRED_DOCUMENTS.map((docReq) => {
            const status = getDocumentStatus(docReq.type);
            const docInfo = getDocumentInfo(docReq.type);
            
            return (
              <div key={docReq.type} className="border border-border p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    {getStatusIcon(status)}
                    <div>
                      <p className="font-medium">
                        {docReq.name}
                        {docReq.required && <span className="text-red-500 ml-1">*</span>}
                      </p>
                      {docInfo?.file_name && (
                        <p className="text-sm text-muted-foreground">{docInfo.file_name}</p>
                      )}
                      {docInfo?.rejection_reason && (
                        <p className="text-sm text-red-600 mt-1">
                          Reason: {docInfo.rejection_reason}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <span className={`px-3 py-1 text-xs font-bold uppercase ${getStatusColor(status)}`}>
                      {status === 'not_uploaded' ? 'Not Uploaded' : status}
                    </span>
                    
                    {/* Upload/Re-upload button */}
                    {status !== 'approved' && (
                      <>
                        <input
                          ref={el => fileInputRefs.current[docReq.type] = el}
                          type="file"
                          accept="image/jpeg,image/png,image/webp,application/pdf"
                          className="hidden"
                          onChange={(e) => handleFileSelect(docReq.type, e.target.files[0])}
                          data-testid={`file-input-${docReq.type}`}
                        />
                        <Button
                          variant={status === 'not_uploaded' ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => fileInputRefs.current[docReq.type]?.click()}
                          disabled={uploading === docReq.type}
                          className="rounded-none"
                          data-testid={`upload-${docReq.type}`}
                        >
                          {uploading === docReq.type ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <>
                              <Upload className="w-4 h-4 mr-1" />
                              {status === 'not_uploaded' ? 'Upload' : 'Re-upload'}
                            </>
                          )}
                        </Button>
                      </>
                    )}
                    
                    {/* Delete button (only for pending/rejected) */}
                    {(status === 'pending' || status === 'rejected') && (
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleDelete(docReq.type)}
                        disabled={deleting === docReq.type}
                        className="rounded-none"
                        data-testid={`delete-${docReq.type}`}
                      >
                        {deleting === docReq.type ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4" />
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <p className="text-sm text-muted-foreground">
          <span className="text-red-500">*</span> Required documents. 
          Supported formats: JPEG, PNG, WebP, PDF. Max size: 5MB.
        </p>
      </div>
    </div>
  );
}
